import React, {
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	Button,
	Divider,
	Form,
	Icon,
	Image,
	Label,
	Message,
	Modal
} from 'semantic-ui-react';

import { stateOptions } from '../../../services/constants';
import {
	validateEmail,
	validatePhone,
	validateZip
} from '../../../services/validation';

import useForm from '../../../hooks/useForm';

import {
	createAgencyStaff,
	updateAgencyStaff,
	updateAgencyStaffPhoto
} from '../../../api/nora/agencies';
import { uploadAsset } from '../../../api/nora/assets';

import { useAgencyState } from '../../../contexts/AgenciesContext';
import { useUIAction } from '../../../contexts/UIContext';

import Alert from '../../Alert';
import DDFileUploadModal from '../../DDFileUpload';
import EmailValidationInfoPopup from '../../Form/EmailValidationInfoPopup';
import FormCheckbox from '../../Form/FormCheckbox';
import FormInput from '../../Form/FormInput';
import FormSelect from '../../Form/FormSelect';
import PhoneValidationInfoPopup from '../../Form/PhoneValidationInfoPopup';

import styles from '../agency-portal.module.css';

const StaffModal = ( {
	onSave,
	open,
	closeModal,
	employee
} ) => {
	const { agency } = useAgencyState();
	const { setApiErrorAlert } = useUIAction();

	const [ saving, setSaving ] = useState( false );
	const [ forceValidation, setForceValidation ] = useState( false );
	const [ fileUploadModal, setFileUploadModal ] = useState( false );
	const [ removePhotoConfirmAlert, setRemovePhotoConfirmAlert ] = useState( false );

	const {
		formData,
		hasChanged: formDataHasChanged,
		updateField,
		setFormData
	} = useForm( {} );

	useEffect( () => {
		setFormData( employee?.id
			? {
				...employee,
				phone: employee.phone.number,
				ext: employee.phone.extension,
				SMSEnabled: employee.phone.isSMSEnabled,
				fax: employee.fax.number
			}
			: {} );
	}, [
		employee,
		setFormData
	] );

	const errors = useMemo( () => ( {
		firstName: [
			{
				error: !formData.firstName,
				message: 'First Name is a required field.',
				fieldLabel: 'First Name'
			}
		],
		lastName: [
			{
				error: !formData.lastName,
				message: 'Last Name is a required field.',
				fieldLabel: 'Last Name'
			}
		],
		zip: [
			{
				error: Boolean( formData.zip ) && !validateZip( formData.zip ),
				message: 'Please enter a valid, 5-digit zip code.',
				fieldLabel: 'Zip'
			}
		],
		email: [
			{
				error: !formData.email,
				message: 'Email is a required field.',
				fieldLabel: 'Email'
			},
			{
				error: Boolean( formData.email ) && !validateEmail( formData.email ),
				message: <>Please enter a valid email address.&nbsp;<EmailValidationInfoPopup /></>,
				fieldLabel: 'Email'
			}
		],
		phone: [
			{
				error: !formData.phone,
				message: 'Phone is a required field.',
				fieldLabel: 'Phone'
			},
			{
				error: Boolean( formData.phone ) && !validatePhone( formData.phone ),
				message: <>Please enter a valid U.S. phone number.&nbsp;<PhoneValidationInfoPopup /></>,
				fieldLabel: 'Phone'
			}
		],
		fax: [
			{
				error: Boolean( formData.fax ) && !validatePhone( formData.fax ),
				message: <>Please enter a valid U.S. Phone number.&nbsp;<PhoneValidationInfoPopup /></>,
				fieldLabel: 'Fax'
			}
		]
	} ), [
		formData.email,
		formData.fax,
		formData.firstName,
		formData.lastName,
		formData.phone,
		formData.zip
	] );

	const validate = useCallback( () => Object.values( errors ).some( errs => errs.some( err => err.error ) ), [ errors ] );

	const mapFormErrors = useCallback(
		() => Object.values( errors ).map( errors => errors.map( error => error.error
			? <Message.Item key={ error.fieldLabel }>{ error.fieldLabel }</Message.Item>
			: null ) ),
		[ errors ]
	);

	const handleSave = useCallback( async () => {
		setSaving( true );

		if ( validate() ) {
			if ( !forceValidation ) {
				setForceValidation( true );
			}
		} else {
			setForceValidation( false );

			let {
				phone,
				ext,
				SMSEnabled,
				fax,
				...rest
			} = formData;

			const data = {
				...rest,
				phone: {
					number: phone,
					extension: ext,
					isSMSEnabled: SMSEnabled
				},
				fax: { number: fax }
			};

			try {
				if ( employee.id ) {
					await updateAgencyStaff( agency.id, data );
				} else {
					await createAgencyStaff( agency.id, data );
				}
			} catch ( error ) {
				setApiErrorAlert( error );
			}

			await onSave();
			closeModal();
		}

		setSaving( false );
	}, [
		agency.id,
		closeModal,
		employee,
		forceValidation,
		formData,
		onSave,
		setApiErrorAlert,
		validate
	] );

	const handleCloseModal = useCallback( () => {
		closeModal();
	}, [ closeModal ] );

	const handleUploadPhoto = useCallback( () => {
		setFileUploadModal( true );
	}, [] );

	// photo
	const onUpload = useCallback( async ( { uid } ) => {
		if ( formData.id ) {
			try {
				const response = await updateAgencyStaffPhoto( formData.id, uid );

				updateField( {
					target: {
						name: 'photo',
						value: response.uri
					}
				}, true );
			} catch ( err ) {
				setApiErrorAlert( err );
			}
		}
	}, [
		formData.id,
		setApiErrorAlert,
		updateField
	] );

	const handleRemovePhoto = useCallback( () => {
		setRemovePhotoConfirmAlert( true );
	}, [] );

	const removePhoto = useCallback( () => {
		if ( formData.id ) {
			try {
				updateAgencyStaffPhoto( formData.id, 0 );

				updateField( {
					target: {
						name: 'photo',
						value: null
					}
				}, true );
			} catch ( err ) {
				setApiErrorAlert( err );
			}
		}
	}, [
		formData.id,
		setApiErrorAlert,
		updateField
	] );

	const closeFileUploadModal = useCallback( () => {
		setFileUploadModal( false );
	}, [] );

	const closeRemovePhotoConfirmAlert = useCallback( () => {
		setRemovePhotoConfirmAlert( false );
	}, [] );

	const emailIcon = useMemo(
		() => !errors.email.some( error => error.error )
			? <Icon
				color="black"
				link
				name="mail"
				onClick={ () => console.log( 'email contact' ) }
			/>
			: null
		, [ errors.email ]
	);

	const countryCode = useMemo( () => <Label basic><span>+1&nbsp;</span></Label>, [] );

	return (
		<Modal open={ open } className={ styles.staffModal }>
			<Modal.Header>{ employee?.id
				? 'Edit Staff'
				: 'Add Staff'
			}</Modal.Header>
			<Modal.Content scrolling>
				<div className="responsive-contact-form-container">
					<div className="no-print">
						<Image
							size="small"
							src={ formData.photo
								? formData.photo
								: 'https://react.semantic-ui.com/images/wireframe/image.png'
							}
						/>
						{ formData.photo
							? <small className="image-upload-actions">
								<span onClick={ handleUploadPhoto }>edit</span>&nbsp;&nbsp;|&nbsp;&nbsp;<span onClick={ handleRemovePhoto }>remove</span>
							</small>
							: <small className="image-upload-actions">
								<span onClick={ handleUploadPhoto }>upload</span>
							</small>
						}
					</div>

					<Form autoComplete="off" error={ forceValidation && validate() }>

						<input
							className="hidden-input"
							name="name"
						/>
						<Form.Group>
							<FormInput
								errors={ errors.firstName }
								fieldLabel="First Name"
								forceValidation={ forceValidation }
								maxLength={ 255 }
								name="firstName"
								onChange={ updateField }
								required
								value={ formData.firstName || '' }
								width={ 6 }
							/>
							<FormInput
								errors={ errors.lastName }
								fieldLabel="Last Name"
								forceValidation={ forceValidation }
								maxLength={ 255 }
								name="lastName"
								onChange={ updateField }
								required
								value={ formData.lastName || '' }
								width={ 6 }
							/>
						</Form.Group>

						<Form.Group>
							<FormInput
								fieldLabel="Title"
								name="title"
								onChange={ updateField }
								value={ formData.title || '' }
								width={ 6 }
							/>
						</Form.Group>

						<Divider hidden style={ { margin: '1.5rem 0' } } />

						<Form.Group>
							<FormInput
								fieldLabel="Address"
								name="address"
								onChange={ updateField }
								value={ formData.address || '' }
								width={ 10 }
							/>
						</Form.Group>

						<Form.Group>
							<FormInput
								fieldLabel="Address Line 2"
								name="address2"
								onChange={ updateField }
								value={ formData.address2 || '' }
								width={ 10 }
							/>
						</Form.Group>

						<Form.Group>
							<FormInput
								fieldLabel="City"
								name="city"
								onChange={ updateField }
								value={ formData.city || '' }
								width={ 6 }
							/>
							<FormSelect
								fieldLabel="State"
								maxLength={ 2 }
								name="state"
								onChange={ updateField }
								options={ stateOptions }
								search
								value={ formData.state || '' }
								width={ 2 }
							/>
							<FormInput
								errors={ errors.zip }
								fieldLabel="Zip"
								forceValidation={ forceValidation }
								keyDown="integer"
								maxLength={ 5 }
								name="zip"
								onChange={ updateField }
								value={ formData.zip || '' }
								width={ 2 }
							/>
						</Form.Group>

						<Divider hidden style={ { margin: '1.5rem 0' } } />

						<Form.Group>
							<FormInput
								errors={ errors.email }
								fieldLabel="Email"
								forceValidation={ forceValidation }
								icon={ emailIcon }
								maxLength={ 254 }
								name="email"
								required
								value={ formData.email || '' }
								onChange={ updateField }
								width={ 8 }
							/>
						</Form.Group>

						<Form.Group>
							<FormInput
								errors={ errors.phone }
								fieldLabel="Phone"
								forceValidation={ forceValidation }
								keyDown="integer"
								inputLabel={ countryCode }
								mask="phone"
								maxLength={ 14 }
								name="phone"
								onChange={ updateField }
								required
								value={ formData.phone || '' }
								width={ 6 }
							/>
							<FormInput
								fieldLabel="Ext."
								forceValidation={ forceValidation }
								keyDown="integer"
								name="ext"
								onChange={ updateField }
								value={ formData.ext || '' }
								width={ 3 }
							/>
							<FormCheckbox
								checked={ formData.SMSEnabled }
								fieldLabel="SMS Enabled"
								name="SMSEnabled"
								onChange={ updateField }
								width={ 3 }
							/>
						</Form.Group>
						<Form.Group>
							<FormInput
								errors={ errors.fax }
								fieldLabel="Fax"
								forceValidation={ forceValidation }
								keyDown="integer"
								inputLabel={ countryCode }
								mask="phone"
								maxLength={ 14 }
								name="fax"
								onChange={ updateField }
								value={ formData.fax || '' }
								width={ 6 }
							/>
						</Form.Group>
						<Message
							error
							size="small"
						>
							<Message.Header>
									There are errors in your submission. Please correct the following fields.
							</Message.Header>
							<br />
							<Message.List>
								{ mapFormErrors() }
							</Message.List>
						</Message>
					</Form>
				</div>
				<DDFileUploadModal
					closeModal={ closeFileUploadModal }
					onUpload={ onUpload }
					open={ fileUploadModal }
					upload={ uploadAsset }
				/>
				<Alert
					action={ removePhoto }
					actionText="Remove Photo"
					name="contact-remove-photo-confirm-alert"
					message="Are you sure?"
					open={ removePhotoConfirmAlert }
					closeModal={ closeRemovePhotoConfirmAlert }
					closeText="Cancel"
					type="confirm"
				/>
			</Modal.Content>
			<Modal.Actions>
				<Button
					content="Save"
					disabled={ !formDataHasChanged }
					loading={ saving }
					onClick={ handleSave }
				/>
				<Button
					content="Cancel"
					onClick={ handleCloseModal }
				/>
			</Modal.Actions>
		</Modal>
	);
};

StaffModal.propTypes = {
	employee: PropTypes.object,
	onSave: PropTypes.func.isRequired,
	open: PropTypes.bool,
	closeModal: PropTypes.func.isRequired
};

export default StaffModal;
